import React from 'react';
import {
  Dashboard,
  AccountBalance,
  TrendingUp,
  Today,
  ShoppingCart,
  Assignment,
  Gavel,
  AccountTree,
  BookOnline,
  MenuBook,
  Assessment,
  Receipt,
  MonetizationOn,
  People,
  LocalShipping,
  Description,
  Payment,
  BarChart,
  Lightbulb,
  RequestQuote,
  AssignmentReturn,
  EventNote,
  Group,
  AttachMoney,
  Timer,
  Inventory,
  Warehouse,
  SwapHoriz,
  ViewList,
  AdminPanelSettings,
  Timeline,
  ManageAccounts,
  Badge,
  AccessTime,
  BeachAccess,
  Payments,
  WorkOutline,
  Handshake,
  PersonSearch,
  FactCheck,
  Inventory2,
  Rule,
  Factory,
  PrecisionManufacturing,
  Calculate,
  Summarize,
  Extension,
  Bolt,
  VpnKey,
  ImportExport,
  AutoAwesomeMosaic,
  CompareArrows,
  AccountBalanceWallet,
  Savings,
  CreditScore,
  CurrencyExchange,
  Public,
  WarningAmber,
  PlaylistAddCheck,
  Insights,
  ReceiptLong,
  Schema,
  Route,
  Analytics,
  Handyman,
  Build,
  Outbox,
  Tune,
  ExitToApp,
  Engineering,
  UploadFile,
  CalendarMonth,
  Campaign,
  EmojiEvents,
  WorkspacePremium,
  Blender,
  HomeOutlined,
  SettingsRounded,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/ModulesContext';

/**
 * The single source of truth for authenticated navigation.
 *
 * Lifted verbatim out of `Sidebar.tsx` during the shell redesign so that the
 * rail AND the top bar's apps-grid render from ONE list. Two hand-maintained
 * copies of a 24-module IA is how a route silently disappears from one surface
 * and not the other.
 *
 * Everything about the items below — text, path, order within its section,
 * `roles`, `moduleKey`, `key` (nav-override anchor) and the manufacturing
 * lifecycle `group` headers — is unchanged from the pre-redesign sidebar. The
 * ONLY additions are:
 *   - `section`, which replaces the old free-text `section` eyebrow with a
 *     fixed enum so items can be bucketed into the new IA, and
 *   - the `Home` item, which points at the already-existing `/home` role-home
 *     redirect route that previously had no nav entry at all.
 */

export type NavSectionId = 'primary' | 'modules' | 'insights' | 'admin';

/** Render order of the rail's sections, with the eyebrow each one prints.
 *  `primary` is label-less on purpose — it is the top block, not a category. */
export const NAV_SECTIONS: Array<{ id: NavSectionId; label?: string }> = [
  { id: 'primary' },
  { id: 'modules', label: 'Business Modules' },
  { id: 'insights', label: 'Insights' },
  { id: 'admin', label: 'Administration' },
];

export interface SubMenuItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  /**
   * Catalogue navItem key (moduleCatalogue.js). Present on relocatable items so
   * a tenant nav override can find and move them between sections.
   */
  key?: string;
  /**
   * Optional lifecycle sub-group label (e.g. 'Masters', 'Planning',
   * 'Production'). When the value changes between consecutive sub-items, a small
   * caption header is printed above — letting a large module's flyout read as an
   * ordered process instead of a flat list. Absent = no header (unchanged look).
   */
  group?: string;
}

export interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  subItems?: SubMenuItem[];
}

export interface MenuItemWithRole extends MenuItem {
  roles?: string[];
  /** Which rail section this item is listed under. */
  section: NavSectionId;
  /**
   * The à-la-carte module this item belongs to (moduleCatalogue.js key). When
   * set, the item is hidden unless the tenant has that module enabled. Absent
   * = always shown (core sections: Dashboard, Reports, Administration).
   */
  moduleKey?: string;
}

export const allMenuItems: MenuItemWithRole[] = [
  {
    // `/home` already existed as the post-login role router (App.tsx) but was
    // unreachable from the nav. It resolves to the caller's own operational
    // control room, so it is a real destination, not a placeholder.
    text: 'Home',
    icon: <HomeOutlined />,
    path: '/home',
    section: 'primary',
  },
  {
    text: 'Command Center',
    icon: <PrecisionManufacturing />,
    path: '/command-center',
    section: 'primary',
  },
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
    section: 'primary',
  },
  {
    text: 'Approvals',
    icon: <FactCheck />,
    path: '/approvals/pending',
    section: 'primary',
  },
  {
    text: 'Reports',
    icon: <Assessment />,
    path: '/reports',
    section: 'insights',
  },
  {
    text: 'Administration',
    icon: <AdminPanelSettings />,
    path: '/admin',
    section: 'admin',
    roles: ['SUPER_ADMIN'],
    subItems: [
      { text: 'Activity Dashboard', path: '/admin/dashboard', icon: <Timeline /> },
      { text: 'User Management', path: '/admin/users', icon: <ManageAccounts /> },
    ],
  },
  {
    text: 'Finance',
    icon: <AccountBalance />,
    path: '/finance',
    section: 'modules',
    moduleKey: 'finance',
    subItems: [
      { text: 'Command Center', path: '/finance', icon: <Dashboard /> },
      // General Ledger
      { text: 'Journal Entries', path: '/finance/journal', icon: <BookOnline /> },
      { text: 'General Ledger', path: '/finance/ledger', icon: <MenuBook /> },
      { text: 'Chart of Accounts', path: '/finance/accounts', icon: <AccountTree /> },
      { text: 'Recurring Journals', path: '/finance/recurring-journals', icon: <Timeline /> },
      // Receivables / Payables
      { text: 'Credit / Debit Notes', path: '/finance/credit-debit-notes', icon: <Receipt /> },
      { text: 'Collections', path: '/finance/collections', icon: <MonetizationOn /> },
      { text: 'Customer Payments', path: '/finance/customer-payments', icon: <ReceiptLong /> },
      { text: 'AR / AP Ageing', path: '/finance/ageing', icon: <Assessment /> },
      // Taxation
      { text: 'GST', path: '/finance/gst-reports', icon: <Gavel /> },
      { text: 'TDS', path: '/finance/tds-reports', icon: <MonetizationOn /> },
      // Budgeting & Costing
      { text: 'Budgets', path: '/finance/budgets', icon: <Calculate /> },
      { text: 'Cost Centers', path: '/finance/cost-centers', icon: <AttachMoney /> },
      // Period Close
      { text: 'Period Close', path: '/finance/period-close', icon: <Rule /> },
      // Reports
      { text: 'Financial Reports', path: '/finance/reports', icon: <Assessment /> },
      { text: 'Day Book', path: '/finance/day-book', icon: <MenuBook /> },
      { text: 'Bank Reconciliation', path: '/finance/bank-reconciliation', icon: <Rule /> },
      { text: 'Expenses', path: '/finance/expenses', icon: <RequestQuote /> },
      { text: 'HSN Codes', path: '/finance/hsn-codes', icon: <Receipt /> },
      { text: 'Bill Allocation', path: '/finance/payment-allocations', icon: <AccountBalanceWallet /> },
      { text: 'Bank Import', path: '/finance/bank-statements', icon: <UploadFile /> },
    ],
  },
  {
    text: 'Fixed Assets',
    icon: <Factory />,
    path: '/assets',
    section: 'modules',
    roles: ['SUPER_ADMIN', 'ADMIN', 'PROCUREMENT', 'FINANCE'],
    moduleKey: 'fixed_assets',
    subItems: [
      { text: 'Asset Register', path: '/assets', icon: <Inventory2 /> },
      { text: 'Depreciation Workbench', path: '/assets/depreciation', icon: <Calculate /> },
      { text: 'Physical Verification', path: '/assets/verification', icon: <Inventory2 /> },
      { text: 'GL Reconciliation', path: '/assets/reconciliation', icon: <AccountTree /> },
      { text: 'IT Blocks', path: '/assets/blocks', icon: <AccountTree /> },
      { text: 'Reports', path: '/assets/reports', icon: <Summarize /> },
    ],
  },
  {
    text: 'Treasury',
    icon: <AccountBalanceWallet />,
    path: '/treasury',
    section: 'modules',
    roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
    moduleKey: 'treasury',
    subItems: [
      { text: 'Treasury Dashboard', path: '/treasury', icon: <Dashboard /> },
      { text: 'Executive MIS', path: '/treasury/executive-mis', icon: <Assessment /> },
      { text: 'Cash Management', path: '/treasury/cash', icon: <AccountBalanceWallet /> },
      { text: 'Bank Accounts', path: '/treasury/banks', icon: <AccountBalance /> },
      { text: 'Reconciliation', path: '/treasury/reconciliation', icon: <Rule /> },
      { text: 'Payments', path: '/treasury/payments', icon: <Payment /> },
      { text: 'Receivables & Payables', path: '/treasury/ar-ap', icon: <SwapHoriz /> },
      { text: 'Investments', path: '/treasury/investments', icon: <Savings /> },
      { text: 'Loans & Borrowings', path: '/treasury/loans', icon: <CreditScore /> },
      { text: 'Working Capital', path: '/treasury/working-capital', icon: <Assessment /> },
      { text: 'FX Rates', path: '/treasury/fx', icon: <CurrencyExchange /> },
      { text: 'Risk & Compliance', path: '/treasury/risk', icon: <Gavel /> },
    ],
  },
  {
    text: 'Import / Export',
    icon: <Public />,
    path: '/exim',
    section: 'modules',
    roles: ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SALES', 'PROCUREMENT'],
    moduleKey: 'exim',
  },
  {
    text: 'HR & People',
    icon: <Badge />,
    path: '/hr',
    section: 'modules',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Command Center', path: '/hr', icon: <Dashboard /> },
      { text: 'Employees', path: '/hr/employees', icon: <People /> },
      { text: 'Org Chart', path: '/hr/org-chart', icon: <AccountTree /> },
      { text: 'Recruitment', path: '/hr/recruitment-ats', icon: <WorkOutline /> },
      { text: 'My Day', path: '/hr/my-day', icon: <Today /> },
    ],
  },
  {
    text: 'Time & Payroll',
    icon: <AccessTime />,
    path: '/hr/time-payroll',
    section: 'modules',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Command Center', path: '/hr/time-payroll', icon: <Dashboard /> },
      { text: 'Attendance Control', path: '/hr/attendance-control', icon: <Insights /> },
      { text: 'Attendance (Daily)', path: '/hr/attendance', icon: <AccessTime /> },
      { text: 'Shifts & Regularization', path: '/hr/shifts', icon: <CalendarMonth /> },
      { text: 'Leave Control', path: '/hr/leave-control', icon: <FactCheck /> },
      { text: 'Leave Requests', path: '/hr/leave', icon: <BeachAccess /> },
      { text: 'Payroll', path: '/hr/payroll', icon: <Payments />, key: 'hr.payroll' },
      { text: 'Salary Structures', path: '/hr/salary-structures', icon: <MonetizationOn /> },
    ],
  },
  {
    text: 'Talent & Lifecycle',
    icon: <EmojiEvents />,
    path: '/hr/performance',
    section: 'modules',
    moduleKey: 'hr_payroll',
    subItems: [
      { text: 'Performance', path: '/hr/performance', icon: <Assessment /> },
      { text: 'Learning', path: '/hr/learning', icon: <MenuBook /> },
      { text: 'Lifecycle', path: '/hr/lifecycle', icon: <ExitToApp /> },
      { text: 'HR Analytics', path: '/hr/analytics', icon: <Insights /> },
    ],
  },
  {
    text: 'CRM',
    icon: <Handshake />,
    path: '/crm',
    section: 'modules',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Command Center', path: '/crm', icon: <Dashboard /> },
      { text: 'Leads', path: '/crm/leads', icon: <PersonSearch /> },
      { text: 'Pipeline', path: '/crm/pipeline', icon: <Timeline /> },
      { text: 'Activities', path: '/crm/activities', icon: <EventNote /> },
      { text: 'My Day', path: '/crm/my-day', icon: <Today /> },
      { text: 'Tasks', path: '/crm/tasks', icon: <Assignment /> },
      { text: 'Calendar', path: '/crm/calendar', icon: <CalendarMonth /> },
    ],
  },
  {
    text: 'Revenue Ops',
    icon: <Insights />,
    path: '/crm/leaderboard',
    section: 'modules',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Forecast', path: '/crm/forecast', icon: <Insights /> },
      { text: 'Forecast Submission', path: '/crm/forecast-submission', icon: <Summarize /> },
      { text: 'Leaderboard', path: '/crm/leaderboard', icon: <EmojiEvents /> },
      { text: 'Territories', path: '/crm/territories', icon: <Public /> },
      { text: 'Win / Loss', path: '/crm/win-loss', icon: <Assessment /> },
      { text: 'Campaigns', path: '/crm/campaigns', icon: <Campaign /> },
      { text: 'Approvals', path: '/crm/approvals', icon: <FactCheck /> },
    ],
  },
  {
    text: 'Sales',
    icon: <TrendingUp />,
    path: '/sales',
    section: 'modules',
    moduleKey: 'sales_crm',
    subItems: [
      { text: 'Command Center', path: '/sales', icon: <Dashboard /> },
      { text: 'Products', path: '/sales/products', icon: <Inventory /> },
      { text: 'UOM Master', path: '/sales/uom-master', icon: <Inventory /> },
      { text: 'Price Lists', path: '/sales/price-lists', icon: <RequestQuote /> },
      { text: 'Customers', path: '/sales/customers', icon: <People /> },
      { text: 'Opportunities', path: '/sales/opportunities', icon: <Lightbulb /> },
      { text: 'Quotations', path: '/sales/quotations', icon: <RequestQuote /> },
      { text: 'Sales Orders', path: '/sales/orders', icon: <Description /> },
      { text: 'Enquiries', path: '/sales/enquiries', icon: <PersonSearch /> },
      { text: 'Contracts', path: '/sales/contracts', icon: <Gavel /> },
      { text: 'Deliveries', path: '/sales/deliveries', icon: <LocalShipping /> },
      { text: 'Invoices', path: '/sales/invoices', icon: <Receipt /> },
      { text: 'Sales Returns', path: '/sales/returns', icon: <AssignmentReturn /> },
      { text: 'Sales Reports', path: '/sales/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Procurement',
    icon: <ShoppingCart />,
    path: '/procurement',
    section: 'modules',
    moduleKey: 'procurement',
    subItems: [
      { text: 'Command Center', path: '/procurement', icon: <Dashboard /> },
      { text: 'Requisitions', path: '/procurement/requisitions', icon: <PlaylistAddCheck /> },
      { text: 'RFQ / Sourcing', path: '/procurement/rfqs', icon: <CompareArrows /> },
      { text: 'Purchase Orders', path: '/procurement/orders', icon: <Description /> },
      { text: 'Goods Receipts', path: '/procurement/goods-receipts', icon: <Inventory2 /> },
      { text: 'Service Entries', path: '/procurement/service-entries', icon: <ReceiptLong /> },
      { text: 'Three-Way Match', path: '/procurement/match', icon: <Rule /> },
      { text: 'Purchase Invoices', path: '/procurement/invoices', icon: <Receipt /> },
      { text: 'Vendor Payments', path: '/procurement/payments', icon: <Payment /> },
      { text: 'Service Work Orders', path: '/procurement/service-work-orders', icon: <Engineering /> },
      { text: 'Service Bill Passing', path: '/procurement/service-bill-passing', icon: <FactCheck /> },
      { text: 'Payment Proposal', path: '/procurement/payments/proposal', icon: <AccountBalanceWallet /> },
      { text: 'Exceptions', path: '/procurement/exceptions', icon: <WarningAmber /> },
      { text: 'Analytics', path: '/procurement/analytics', icon: <Insights /> },
      { text: 'Vendors', path: '/procurement/vendors', icon: <LocalShipping /> },
      { text: 'Vendor Onboarding', path: '/procurement/vendor-onboarding', icon: <FactCheck /> },
      { text: 'TDS Management', path: '/procurement/tds', icon: <MonetizationOn />, key: 'proc.tds' },
      { text: 'Reports', path: '/procurement/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Projects',
    icon: <Assignment />,
    path: '/projects',
    section: 'modules',
    moduleKey: 'projects',
    subItems: [
      { text: 'Command Center', path: '/projects', icon: <Dashboard /> },
      { text: 'Portfolio', path: '/projects/portfolio', icon: <ViewList /> },
      { text: 'All Projects', path: '/projects/all', icon: <EventNote /> },
      { text: 'My Day', path: '/projects/my-day', icon: <Today /> },
      { text: 'Tasks', path: '/projects/tasks', icon: <Assignment /> },
      { text: 'Resources', path: '/projects/resources', icon: <Group /> },
      { text: 'Cost Tracking', path: '/projects/costs', icon: <AttachMoney /> },
      { text: 'Time Tracking', path: '/projects/time', icon: <Timer /> },
      { text: 'Project Reports', path: '/projects/reports', icon: <BarChart /> },
    ],
  },
  {
    text: 'Inventory',
    icon: <Inventory />,
    path: '/inventory',
    section: 'modules',
    moduleKey: 'inventory',
    subItems: [
      { text: 'Command Center', path: '/inventory', icon: <Dashboard /> },
      { text: 'Stock List', path: '/inventory/stock', icon: <ViewList /> },
      { text: 'Opening Stock', path: '/inventory/opening-stock/new', icon: <Inventory /> },
      { text: 'Stock Movements', path: '/inventory/movements', icon: <SwapHoriz /> },
      { text: 'Warehouses', path: '/inventory/warehouses', icon: <Warehouse /> },
      { text: 'Reorder Planning', path: '/inventory/reorder', icon: <PlaylistAddCheck /> },
      { text: 'Expiry', path: '/inventory/expiry', icon: <WarningAmber /> },
      { text: 'Serial Numbers', path: '/inventory/serials', icon: <Inventory2 /> },
      // Material conversion / reclassification — the transaction that replaces
      // editing a product's type on the master.
      { text: 'Material Conversion', path: '/inventory/conversions', icon: <SwapHoriz /> },
      { text: 'Analytics', path: '/inventory/analytics', icon: <Analytics /> },
    ],
  },
  {
    text: 'SCM / Stores',
    icon: <Warehouse />,
    path: '/scm',
    section: 'modules',
    moduleKey: 'inventory',
    subItems: [
      { text: 'Material Requisitions', path: '/scm/material-requisitions', icon: <Assignment /> },
      { text: 'Material Issues', path: '/scm/material-issues', icon: <Outbox /> },
      { text: 'Loan Issues', path: '/scm/loan-issues', icon: <Handshake /> },
      { text: 'Stock Adjustments', path: '/scm/stock-adjustments', icon: <Tune /> },
      { text: 'Stock Transfers', path: '/scm/stock-transfers', icon: <SwapHoriz /> },
      { text: 'Stock Verification', path: '/scm/stock-counts', icon: <FactCheck /> },
      { text: 'Tools & Tackles', path: '/scm/tools', icon: <Build /> },
      { text: 'Gate Pass', path: '/scm/gate-passes', icon: <ExitToApp /> },
      { text: 'Reason Codes', path: '/scm/reason-codes', icon: <Rule /> },
    ],
  },
  {
    text: 'Manufacturing',
    icon: <PrecisionManufacturing />,
    path: '/manufacturing',
    section: 'modules',
    moduleKey: 'manufacturing',
    subItems: [
      // Ordered by the manufacturing lifecycle: configure → plan → produce →
      // declare → inspect → maintain → measure. `group` prints a stage header.
      { text: 'Dashboard', path: '/manufacturing/dashboard', icon: <Dashboard />, group: 'Overview' },
      { text: 'Control Tower', path: '/manufacturing', icon: <Assessment />, group: 'Overview' },
      { text: 'Lot Traceability', path: '/manufacturing/traceability', icon: <AccountTree />, group: 'Overview' },

      { text: 'BOM', path: '/manufacturing/bom', icon: <Schema />, group: 'Master Data' },
      { text: 'Routing', path: '/manufacturing/routing', icon: <Route />, group: 'Master Data' },
      { text: 'Work Centers', path: '/manufacturing/work-centers', icon: <Factory />, group: 'Master Data' },
      { text: 'Equipment / Machines', path: '/manufacturing/maintenance/equipment', icon: <Handyman />, group: 'Master Data' },
      { text: 'Lines · Ops · Shifts', path: '/manufacturing/masters', icon: <ViewList />, group: 'Master Data' },
      { text: 'QC Parameters', path: '/manufacturing/qc-parameters', icon: <Tune />, group: 'Master Data' },
      { text: 'Item QC Specs', path: '/manufacturing/qc-item-parameters', icon: <Rule />, group: 'Master Data' },
      { text: 'Quality Grades', path: '/manufacturing/quality-grades', icon: <WorkspacePremium />, group: 'Master Data' },

      { text: 'Planning Workbench', path: '/manufacturing/planning', icon: <Insights />, group: 'Planning' },
      { text: 'MRP / Material Planning', path: '/manufacturing/mrp', icon: <Analytics />, group: 'Planning' },

      { text: 'Production Orders', path: '/manufacturing/work-orders', icon: <Description />, group: 'Production' },
      { text: 'Production Process', path: '/manufacturing/production-process', icon: <Factory />, group: 'Production' },
      { text: 'Production Declaration', path: '/manufacturing/production-declaration', icon: <Engineering />, group: 'Production' },
      { text: 'Blending', path: '/manufacturing/blending', icon: <Blender />, group: 'Production' },
      { text: 'Packing', path: '/manufacturing/packing', icon: <Inventory2 />, group: 'Production' },

      // Subcontracting lives under its own /subcontracting shell but belongs in
      // the manufacturing lifecycle nav — it is a production stage, not a
      // separate department.
      { text: 'Subcontracting', path: '/subcontracting', icon: <Handyman />, group: 'Subcontracting' },
      { text: 'Subcontract Orders', path: '/subcontracting/orders', icon: <Assignment />, group: 'Subcontracting' },
      { text: 'Job Work Challans', path: '/subcontracting/challans', icon: <LocalShipping />, group: 'Subcontracting' },
      { text: 'Job Work Receipts', path: '/subcontracting/receipts', icon: <Inventory2 />, group: 'Subcontracting' },

      { text: 'Quality Control Tower', path: '/manufacturing/quality', icon: <FactCheck />, group: 'Quality' },
      { text: 'Inspection Lots', path: '/manufacturing/quality/lots', icon: <Rule />, group: 'Quality' },
      { text: 'Quality Checks', path: '/manufacturing/quality-checks', icon: <PlaylistAddCheck />, group: 'Quality' },
      { text: 'NCR / CAPA', path: '/manufacturing/quality/ncr', icon: <WarningAmber />, group: 'Quality' },

      { text: 'Maintenance Control', path: '/manufacturing/maintenance', icon: <Build />, group: 'Maintenance' },
      { text: 'PM Schedules', path: '/manufacturing/maintenance/schedules', icon: <EventNote />, group: 'Maintenance' },
      { text: 'Maintenance Orders', path: '/manufacturing/maintenance/work-orders', icon: <Assignment />, group: 'Maintenance' },

      { text: 'OEE & Downtime', path: '/manufacturing/oee', icon: <Timeline />, group: 'Performance' },

      { text: 'Production Costing', path: '/manufacturing/costing', icon: <Calculate />, group: 'Costing' },
      { text: 'Standard Costs', path: '/manufacturing/standard-costs', icon: <MonetizationOn />, group: 'Costing' },

      { text: 'Manufacturing Reports', path: '/manufacturing/reports', icon: <Summarize />, group: 'Reports' },
    ],
  },
  {
    text: 'Compliance',
    icon: <Gavel />,
    path: '/compliance',
    section: 'modules',
    moduleKey: 'compliance',
  },
  {
    text: 'Workflows',
    icon: <AccountTree />,
    path: '/workflows',
    section: 'admin',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    moduleKey: 'workflows',
  },
  {
    text: 'Integrations',
    icon: <Extension />,
    path: '/integrations',
    section: 'admin',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    moduleKey: 'integrations',
    subItems: [
      { text: 'Catalogue', path: '/integrations', icon: <Extension /> },
      { text: 'Connections', path: '/integrations/connections', icon: <SwapHoriz /> },
      { text: 'Webhooks', path: '/integrations/webhooks', icon: <Bolt /> },
      { text: 'API Keys', path: '/integrations/api-keys', icon: <VpnKey /> },
      { text: 'Import / Export', path: '/integrations/import-export', icon: <ImportExport /> },
    ],
  },
  {
    // The marketplace itself. Core platform tooling for the org owner — never
    // gated by an entitlement, only by role.
    text: 'Plan & Modules',
    icon: <AutoAwesomeMosaic />,
    path: '/admin/modules',
    section: 'admin',
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    // `/settings` already existed but was reachable only from the avatar menu,
    // which is not where anyone looks for company + preference configuration.
    // Ungated, exactly as the route is ungated in App.tsx.
    text: 'System Settings',
    icon: <SettingsRounded />,
    path: '/settings',
    section: 'admin',
  },
];

/**
 * Role + entitlement filtering and tenant nav-overrides, in one hook.
 *
 * Moved out of the Sidebar component body unchanged so the apps-grid in the top
 * bar gates on exactly the same rules — a module the tenant hasn't bought must
 * not appear in the grid either. The behaviour is identical to the pre-redesign
 * sidebar; only its address changed.
 */
export const useNavItems = (): MenuItemWithRole[] => {
  const { user } = useAuth();
  const { isModuleEnabled, navOverrides } = useModules();

  // Filter menu items by role AND by the tenant's enabled modules. An item
  // with a moduleKey the tenant hasn't purchased is hidden here — the single
  // choke-point — and its routes are separately blocked by <ModuleGuard>.
  const filteredItems = React.useMemo(() => {
    return allMenuItems.filter((item) => {
      if (item.roles && !(user?.role && item.roles.includes(user.role))) return false;
      if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return false;
      return true;
    });
  }, [user?.role, isModuleEnabled]);

  // Apply the tenant's relocation overrides (Phase 2): pull a relocatable
  // sub-item out of its home section and re-home it under the target section.
  // A move whose destination isn't currently visible (module off / role-hidden)
  // is skipped, so the item never vanishes. The item's route is unchanged — only
  // where it is *listed* moves.
  return React.useMemo(() => {
    const entries = Object.entries(navOverrides || {});
    if (entries.length === 0) return filteredItems;

    // Clone items with fresh subItems arrays so we never mutate allMenuItems.
    const items = filteredItems.map((it) => ({
      ...it,
      subItems: it.subItems ? [...it.subItems] : it.subItems,
    }));
    const byPath = new Map(items.map((it) => [it.path, it]));

    for (const [navKey, targetPath] of entries) {
      const target = byPath.get(targetPath);
      if (!target) continue; // destination section not visible right now
      let moved: SubMenuItem | null = null;
      for (const it of items) {
        if (!it.subItems) continue;
        const idx = it.subItems.findIndex((s) => s.key === navKey);
        if (idx >= 0) {
          moved = it.subItems[idx];
          it.subItems.splice(idx, 1);
          break;
        }
      }
      if (!moved) continue;
      if (!target.subItems) target.subItems = [];
      if (!target.subItems.some((s) => s.key === navKey)) target.subItems.push(moved);
    }
    return items;
  }, [filteredItems, navOverrides]);
};

/**
 * A click that carries a browser "open elsewhere" intent — Ctrl/Cmd/Shift/Alt,
 * or a middle-click. When the nav item is a real <a href> (component=RouterLink),
 * letting these fall through hands control to the browser so it opens a new tab
 * / window natively instead of us hijacking it into an in-app navigation.
 */
export const isModifiedClick = (e: React.MouseEvent) =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
